import React, { useLayoutEffect, useRef, useState } from 'react';

import { ChatDisplayItem } from '~/app/selectors';

import ChatAvatar from './ChatAvatar';
import { ChatGroupStickyCompactBar, ChatGroupStickyWideBar } from './ChatGroupStickyHeader';
import ChatMessageRow from './ChatMessageRow';
import { useChatStickyHeaderPinned } from './useChatStickyHeaderPinned';

export type MessageItem = ChatDisplayItem & {
  type: 'message';
  model: NonNullable<ChatDisplayItem['model']>;
  userId: string;
};

export interface ChatMessageGroupProps {
  messages: MessageItem[];
  unknownPlayerLabel: string;
}

const ChatMessageGroup: React.FC<ChatMessageGroupProps> = ({ messages, unknownPlayerLabel }) => {
  const first = messages[0];
  const senderDisplayName = (first.model.sender ?? '').trim()
    ? first.model.sender!
    : unknownPlayerLabel;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const incomingWideHeaderRef = useRef<HTMLDivElement>(null);
  const [stickyAvatarTopPx, setStickyAvatarTopPx] = useState(36);
  const headerPinned = useChatStickyHeaderPinned(sentinelRef);
  const isOutgoing = first.model.direction === 'outgoing';

  useLayoutEffect(() => {
    if (isOutgoing) return;
    const el = incomingWideHeaderRef.current;
    if (!el) return;
    const update = () => {
      const h = el.offsetHeight;
      if (h > 0) setStickyAvatarTopPx(h);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOutgoing, first.id, senderDisplayName]);

  const messageRows = messages.map((msg, mi) => {
    const startsGroup = mi === 0;
    const endsGroup = mi === messages.length - 1;
    const rowSender = (msg.model.sender ?? '').trim() ? msg.model.sender! : unknownPlayerLabel;
    return (
      <ChatMessageRow
        key={msg.id}
        item={msg}
        senderDisplayName={rowSender}
        startsGroup={startsGroup}
        endsGroup={endsGroup}
        suppressGroupHeader={startsGroup}
        wideBubbleColumnOnly
      />
    );
  });

  return (
    <div className="flex min-w-0 flex-col gap-0.5 @[18rem]:gap-1">
      <div
        ref={sentinelRef}
        data-chat-sticky-sentinel=""
        className="pointer-events-none h-px w-full shrink-0 overflow-hidden opacity-0"
        aria-hidden
      />
      <ChatGroupStickyCompactBar
        item={first}
        senderDisplayName={senderDisplayName}
        headerPinned={headerPinned}
      />
      <ChatGroupStickyWideBar
        ref={isOutgoing ? null : incomingWideHeaderRef}
        item={first}
        senderDisplayName={senderDisplayName}
        headerPinned={headerPinned}
      />
      {isOutgoing ? (
        <div className="flex min-w-0 flex-col gap-0.5 @[18rem]:gap-1">{messageRows}</div>
      ) : (
        <div className="flex w-full min-w-0 flex-col gap-0.5 @[18rem]:flex-row @[18rem]:items-start @[18rem]:gap-2">
          <div
            className="hidden w-9 shrink-0 self-start @[18rem]:sticky @[18rem]:z-8 @[18rem]:block"
            style={
              {
                top: `${stickyAvatarTopPx}px`,
              } as React.CSSProperties
            }
            data-chat-sticky-avatar=""
          >
            <ChatAvatar
              userId={first.userId}
              username={senderDisplayName}
              size="lg"
              className="pointer-events-auto h-9 w-9 bg-transparent"
            />
          </div>
          <div className="flex w-full min-w-0 flex-1 flex-col gap-0.5 @[18rem]:gap-1">
            {messageRows}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessageGroup;
